import type { LocaleCode } from '@/i18n/locales'
import { normalize, stripLeadIn, words } from '@/voice/nlu/normalize'

/**
 * What the conversation was about, in a few words.
 *
 * Deliberately conservative, and it returns nothing far more often than it
 * returns something. The two failures are not symmetrical. Returning nothing
 * costs one sentence of a greeting nobody knew was coming. Returning the wrong
 * thing makes Namu open with "last time we spoke about um I think the thing",
 * out loud, before the person has any way to stop it, and there is no worse
 * moment to sound like a machine reading its own notes back.
 *
 * This is a placeholder for a job a language model does properly, and it is
 * written as one: when the model lands it supplies the topic and this stops
 * being called. It exists rather than being left as a permanent null so that
 * the continuity line is code somebody has actually run in the product, rather
 * than a branch waiting for a backend.
 */

/**
 * Question frames, stripped off the front of the utterance.
 *
 * `stripLeadIn` takes the longest match, so the specific forms can sit
 * alongside the general ones without ordering mattering.
 */
const LEAD_INS: Record<LocaleCode, readonly string[]> = {
  en: [
    'what are',
    'what is',
    "what's",
    'what was',
    'what were',
    'tell me about',
    'tell me',
    'can you tell me about',
    'could you tell me about',
    'do you know about',
    'do you know',
    'i want to know about',
    'i wanted to ask about',
    'i need to know about',
    'i was asking about',
    'how much is',
    'how much are',
    'how do i',
    'how does',
    'how is',
    'when is',
    'when are',
    'when does',
    'where is',
    'where are',
    'where can i find',
    'why is',
    'why are',
    'who is',
    'who are',
    'about',
  ],
  ha: [
    'menene',
    'mene ne',
    'me ne',
    'me ake',
    'me ke',
    'ina ne',
    'ina',
    'yaushe ne',
    'yaushe',
    'nawa ne',
    'nawa',
    'ta yaya',
    'yaya',
    'wanene',
    'wane ne',
    'ka gaya mini game da',
    'gaya mini game da',
    'ka gaya mini',
    'gaya mini',
    'ina son sanin',
    'ina so in san',
    'na so in san',
    'shin ka san',
    'ko ka san',
    'ka san',
    'game da',
  ],
}

/**
 * Words that name nothing on their own.
 *
 * Used to throw away what is left when the question frame was effectively the
 * whole sentence. "What about it" strips to "about it", which is grammatical,
 * short, and describes no subject whatsoever.
 */
const FUNCTION_WORDS: Record<LocaleCode, ReadonlySet<string>> = {
  en: new Set([
    'the', 'a', 'an', 'it', 'that', 'this', 'they', 'them', 'you', 'yourself',
    'me', 'my', 'is', 'are', 'was', 'were', 'about', 'of', 'and', 'or', 'to',
    'in', 'on', 'for', 'some', 'any', 'thing', 'things', 'one', 'there',
  ]),
  ha: new Set([
    'da', 'na', 'ne', 'ce', 'ta', 'ya', 'shi', 'ita', 'su', 'ka', 'ki', 'ni',
    'a', 'ga', 'wannan', 'wancan', 'abu', 'abin', 'kai', 'ke', 'wani', 'wata',
    'game', 'kan', 'yake', 'take', 'ba',
  ]),
}

/**
 * Longer than this and it is a sentence, not a subject.
 *
 * Six words is roughly where "about" stops working as a preposition in front
 * of what follows it.
 */
const MAX_WORDS = 6

/**
 * Reads a topic out of one thing the person said, or gives up.
 *
 * Only utterances that opened with a recognised question frame produce a
 * topic. That single rule is doing most of the work here, and the alternative
 * is why it exists: keeping any short utterance meant "the tomatoes are dying"
 * was stored verbatim and read back as "last time we spoke about the tomatoes
 * are dying". A clause is not a noun phrase, and there is no cheap way to tell
 * the difference from the words alone. Having stripped a question off the
 * front, what remains is what the question was asking about, which is exactly
 * the thing that fits after "about".
 *
 * The result stays lowercase. Restoring capitals would mean running a place or
 * a name back through `titleCase`, which is documented over in `normalize` as
 * the thing that turns Sa'adu into Sa'Adu, and the topic is spoken far more
 * often than it is read.
 */
export function extractTopic(transcript: string, locale: LocaleCode): string | null {
  const text = normalize(transcript)
  if (text.length === 0) return null

  const stripped = stripLeadIn(text, LEAD_INS[locale])
  if (stripped === text) return null

  const tokens = words(stripped)
  if (tokens.length === 0 || tokens.length > MAX_WORDS) return null

  const functionWords = FUNCTION_WORDS[locale]
  if (tokens.every((word) => functionWords.has(word))) return null

  return tokens.join(' ')
}
